import { useMemo, useCallback } from "react";
import { assigneeRefToEntityItem, selectionToUserEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type EntityItemConfig,
  type IconName,
} from "@salestrak/ui";
import { useMemberSelection } from "./use-member-selection";
import type { EntityRefValue } from "#app/lib/data";
import { useEntityDetailOptional } from "#app/components/entity";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

export type AssigneeSelectorProps = Omittable<{
  /** Currently selected member IDs */
  value?: string | string[] | null;
  /** Called when selection changes with an array of entity IDs */
  onValueChange?: (ids: string[]) => void;
  /** Seed selected assignees so current values render even if absent from the pool query */
  selectedRefs?: EntityRefValue[];
  /** Entity IDs to hide from the pool */
  exclude?: string[];
  /** Restrict the pool to active members only (excludes invited/pending). */
  activeOnly?: boolean;
  /** The member statuses to list, in place of `activeOnly`'s choice. */
  statuses?: readonly string[];
  /** Maximum number of selections */
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  /** `"chips"` (default) pins removable chips above the pool; `"list"` uses a vertical selected section. */
  mode?: "chips" | "list";
  /** Whether selected assignees can be reordered to set the primary assignee. */
  allowSetPrimary?: boolean;
  /** Whether to show the Set primary action for every selected assignee. */
  showSetPrimaryOnAll?: boolean;
  /** Whether the selected primary assignee should show "Primary" subtext. */
  showPrimarySubtext?: boolean;
  /** Called when "Set primary" is clicked. Fires the mutation directly without reordering. */
  onSetPrimary?: (entityId: string) => void;
  /** When provided, called instead of opening the internal profile dialog. Use when the selector is rendered inside a popover that would unmount the dialog. */
  onViewProfile?: (userId: string) => void;
  /** Drop the per-row Profile action. Pass false where a seeded selection is
   *  not a workspace user — the action would open the member dialog on an id
   *  no member has. */
  showProfileActions?: boolean;
  placeholder?: string;
  emptyTextVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["emptyTextVariant"];
  size?: SelectorSize;
  disabled?: boolean;
  /** Render selections as removable chips in the trigger. */
  chips?: boolean;
  /** Leading icon shown in the trigger. */
  icon?: IconName;
  /** Stretch trigger to fill its container. */
  fill?: boolean;
  /** Match popover width to trigger. */
  contentFit?: boolean;
  /** Popover min-width preset. */
  contentSize?: EntitySelectorContentSize;
  /**
   * Replace ONLY the trigger, keeping this selector's own pool content. For a
   * trigger that is a piece of a row rather than a labelled control — a bare
   * avatar stack, a hover-revealed add button — where the default
   * value-plus-placeholder button reads as chrome the row does not have.
   *
   * `children` is the other seam and a heavier one: it replaces the trigger AND
   * the content, so a caller who only wants a different press target ends up
   * re-authoring the member list. Reach for this first.
   */
  renderTrigger?: React.ReactElement<Record<string, unknown>>;
  children?: React.ReactNode;
}>;

/**
 * Domain wrapper for selecting issue assignees (members only).
 * Single-pool selector for members, with a guaranteed-but-changeable primary.
 */
function primarySubtext(ref: EntityRefValue, show: boolean) {
  return show && ref.isPrimary ? "Primary" : undefined;
}

export function AssigneeSelector({
  value,
  onValueChange,
  selectedRefs,
  exclude,
  activeOnly,
  statuses,
  maxSelections,
  variant,
  mode = "chips",
  allowSetPrimary = true,
  showSetPrimaryOnAll = false,
  showPrimarySubtext = true,
  onSetPrimary,
  onViewProfile,
  showProfileActions = true,
  placeholder = "Select assignee...",
  emptyTextVariant,
  size,
  disabled,
  chips,
  icon,
  fill,
  contentFit,
  contentSize,
  renderTrigger,
  children,
}: AssigneeSelectorProps) {
  const seeds = useMemo(
    () =>
      selectedRefs?.map((ref) => ({
        ...assigneeRefToEntityItem(ref),
        description: primarySubtext(ref, showPrimarySubtext),
      })),
    [selectedRefs, showPrimarySubtext],
  );
  const { pool: memberPool, selections: memberSelections } = useMemberSelection({
    value,
    seeds,
    exclude,
    activeOnly,
    statuses,
  });
  const detail = useEntityDetailOptional();

  const mergedUserPool = useMemo(
    () => ({
      ...memberPool,
      // closeOnAction: "Profile" opens the member overlay, and a picker left
      // open behind that overlay reads as an unanswered question. The false
      // branch STRIPS the badge the user pool bakes in by default — this
      // selector's seeded selections can carry ids no member has, so the
      // pool-carried action must not survive either.
      itemBadge: showProfileActions
        ? {
            closeOnAction: true,
            label: "Profile",
            tooltip: "Open Profile",
          }
        : undefined,
    }),
    [memberPool, showProfileActions],
  );

  const handleViewProfile = useCallback((entity: EntityItemConfig) => {
    if (onViewProfile) {
      onViewProfile(entity.id);
      return;
    }
    detail?.openEntity("user", entity.id, { via: "assignee_selector" });
  }, [onViewProfile, detail]);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [mergedUserPool],
      maxSelections,
      searchPlaceholder: "Search members...",
      emptyMessage: "No results found",
      noItemsMessage: "No members yet",
    }),
    [mergedUserPool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const selectedRefMap = new Map(
      (selectedRefs ?? []).map((ref) => [ref.entityId, ref]),
    );
    return memberSelections.map((selection) => {
      const selectedRef = selectedRefMap.get(selection.id);
      if (!selectedRef) return selection;
      return {
        ...selection,
        entity: {
          ...selection.entity,
          description: primarySubtext(selectedRef, showPrimarySubtext),
          data: {
            ...selection.entity.data,
            isPrimary: selectedRef.isPrimary ?? false,
          },
        },
      };
    });
  }, [memberSelections, selectedRefs, showPrimarySubtext]);

  const getSetPrimaryLabel = useCallback(
    (selection: EntitySelection) => {
      const index = selections.findIndex((item) => item.id === selection.id);
      if (index < 0) return undefined;
      return showSetPrimaryOnAll || index > 0 ? "Set primary" : undefined;
    },
    [selections, showSetPrimaryOnAll],
  );

  const handleSetPrimary = useCallback(
    (entity: EntityItemConfig) => {
      if (onSetPrimary) {
        onSetPrimary(entity.id);
        return;
      }

      const index = selections.findIndex((selection) => selection.id === entity.id);
      if (index <= 0) return;

      const nextSelections = [
        at(selections, index, "selection"),
        ...selections.slice(0, index),
        ...selections.slice(index + 1),
      ];

      onValueChange?.(nextSelections.map((selection) => selection.id));
    },
    [onSetPrimary, onValueChange, selections],
  );

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <>
      <EntitySelector
        title="Assignees"
        variant={variant}
        config={config}
        value={selections}
        onValueChange={handleValueChange}
      >
        {children ?? (
          <>
            {renderTrigger ? (
              <EntitySelectorTrigger disabled={disabled} render={renderTrigger} />
            ) : chips ? (
              <EntitySelectorTrigger
                placeholder={placeholder}
                size={toEntityTriggerSize(size)}
                disabled={disabled}
                chips
                fill={fill}
                icon={icon}
              />
            ) : (
              <SelectionEntityTrigger
                toEntity={selectionToUserEntity}
                placeholder={placeholder}
                emptyTextVariant={emptyTextVariant}
                title="Assignees"
                emptyIcon={icon}
                size={size}
                disabled={disabled}
                fill={fill}
              />
            )}
            {/* Row actions are wired per list (pool/selected), not on the
                content shell — the drawer variant renders the same children. */}
            <EntitySelectorContent size={contentSize} fit={contentFit}>
              {/* No wrapper: the content shell owns the height cap, and the
                  list — chosen rows seated at the top of the pool — is ONE
                  scroller under it. */}
              {mode === "list" ? (
                <>
                  <EntitySelectorInput />
                  <EntitySelectorSelected
                    label="Assignees"
                    hideWhenEmpty
                    hideBadge
                    hideSelectionHighlight
                    onItemAction={showProfileActions ? handleViewProfile : undefined}
                    getItemSecondaryActionLabel={
                      allowSetPrimary ? getSetPrimaryLabel : undefined
                    }
                    onItemSecondaryAction={
                      allowSetPrimary ? handleSetPrimary : undefined
                    }
                  />
                  <EntitySelectorPool
                    value={mergedUserPool.id}
                    hideSelectionHighlight
                    onItemAction={showProfileActions ? handleViewProfile : undefined}
                  />
                  <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
                </>
              ) : (
                <>
                  <EntitySelectorInput />
                  <EntitySelectorPool
                    value={mergedUserPool.id}
                    onItemAction={showProfileActions ? handleViewProfile : undefined}
                  />
                  <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
                </>
              )}
            </EntitySelectorContent>
          </>
        )}
      </EntitySelector>
    </>
  );
}
